import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ComponentProps, CSSProperties, ReactNode } from 'react'

type SortableProps = ComponentProps<typeof DndContext> & ComponentProps<typeof SortableContext>

export const Sortable = ({
  accessibility,
  autoScroll,
  cancelDrop,
  collisionDetection,
  children,
  disabled,
  id,
  items,
  measuring,
  modifiers,
  onDragStart,
  onDragMove,
  onDragOver,
  onDragEnd,
  onDragCancel,
  sensors,
  strategy = verticalListSortingStrategy
}: SortableProps) => {
  const defaultCollisionDetection = closestCenter

  const defaultModifiers = [restrictToVerticalAxis, restrictToParentElement]

  const defaultSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const defaultStrategy = verticalListSortingStrategy

  return (
    <DndContext
      accessibility={accessibility}
      autoScroll={autoScroll}
      cancelDrop={cancelDrop}
      collisionDetection={collisionDetection ?? defaultCollisionDetection}
      id={id}
      measuring={measuring}
      modifiers={modifiers ?? defaultModifiers}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      sensors={sensors ?? defaultSensors}
    >
      <SortableContext disabled={disabled} id={id} items={items} strategy={strategy ?? defaultStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

type SortableItemProps = Parameters<typeof useSortable>[0] & {
  children: (sortable: ReturnType<typeof useSortable> & { style: CSSProperties }) => ReactNode
}

export const SortableItem = ({
  animateLayoutChanges,
  attributes,
  disabled,
  data,
  getNewIndex,
  id,
  strategy,
  resizeObserverConfig,
  transition,
  children
}: SortableItemProps) => {
  const sortable = useSortable({
    animateLayoutChanges,
    attributes,
    disabled,
    data,
    getNewIndex,
    id,
    strategy,
    resizeObserverConfig,
    transition
  })

  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition
  }

  return children({ ...sortable, style })
}
